import { cn } from "../lib/cn";
import { countryCode, countryFlagUrl, countryName } from "../lib/countries";

export interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 code or English name; empty or unknown shows the placeholder. */
  country: string | null | undefined;
  /** Sizing and spacing. Defaults to a 16×12 px (4:3) flag. */
  className?: string;
  /**
   * Accessible name. Decorative (`""`) by default: the text next to a flag
   * usually names the country or the item it belongs to.
   */
  alt?: string;
  /** Keep the flag's footprint when no flag is available, so rows stay aligned. Default true. */
  placeholder?: boolean;
}

/** A country flag from the shared `@nexus/ui` catalog, with the country name as tooltip. */
export function CountryFlag({
  country,
  className,
  alt = "",
  placeholder = true,
}: CountryFlagProps) {
  const url = countryFlagUrl(country);
  const name = countryName(country);
  const box = cn("inline-block h-3 w-4 shrink-0 rounded-[2px]", className);
  if (!url) {
    if (!placeholder) return null;
    return (
      <span
        aria-hidden="true"
        title={name}
        className={cn(box, "border border-dashed border-border-strong")}
        data-country-flag="missing"
      />
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      title={name}
      loading="lazy"
      draggable={false}
      className={cn(box, "object-cover ring-1 ring-border")}
      data-country-flag={countryCode(country)}
    />
  );
}
